/* eslint-disable @next/next/no-img-element */
import React, { useState } from 'react';
import Head from 'next/head';

import Header from '../../components/Header';
import Footer from '../../components/Footer';
import PageHeader from '../../components/PageHeader';
import ImagePointsSection from '../../components/ImagePointsSection';
import ActionSection from '../../components/ActionSection';
import NevoBanner from '../../components/NevoBanner';
import TheGreatSelector from '../../components/TheGreatSelector';

import eventGTM from '../../lib/gtag';

import styles from './styles.module.scss'
import TelematicsBackground from '../../public/assets/images/telematics-background.jpg';
import TelematicsPageBackground from '../../public/assets/images/telematics-page-background.jpg';
import NevoGeotabImage from '../../public/assets/images/nevo-geolab.png';
import GeolabAfter from '../../public/assets/images/geolab-after.jpg';
import GoDeviceImage from '../../public/assets/images/go-device-in-vehicle-driver-coaching.png';
import OEMTelematicsImage from '../../public/assets/images/scalable-fleet-management-software.png';
import MixedFleetsImage from '../../public/assets/images/geotab-mixed-fleet-management.png';
import links from '../../helper/links';
import PointsSection from '../../components/PointsSection';

const options = [
  "Reduce your fleet’s fuel consumption",
  "Manage costs with improved operations"
]

export default function TelematicsPage()  {
  const [selected, setSelected] = useState(options[0]);

  return (
    <>
      <Head>
        <title>Telematics | Nevo Advisory</title>
      </Head>

      <Header black transparent />

      <PageHeader backgroundImage={TelematicsBackground.src} className={styles.homeChargingHeader}>
        <h1 className='page-header-h1'>Telematics.</h1>
      </PageHeader>

      <div className={`${styles.homeChargingPage} page-holder`}>
        
        <div className={`${styles.geotabPartnerShip} padding`}>
          <img className={styles.nevoGeotabLogo} src={NevoGeotabImage.src} alt='Nevo Geotab Partnership'/>
          <h1 className={`${styles.geotabHeader} perfect-header`}>Vehicle telematics connected your way</h1>
          <p className={`${styles.geotabSubtext} perfect-subtext`}>
            GO device, OEM embedded or third party hardware, Geotab makes vehicle telematics secure and consistent. Connect your fleet with the power of choice
          </p>
        </div>

        <div className="image-section section" style={{ backgroundImage: `url(${TelematicsPageBackground.src})` }}>&nbsp;</div>

        <div className={`${styles.collabDetails} cooler-padding`}>
          <ImagePointsSection 
            switchDirection
            header='Geotab GO Device'
            text={<>
              Capturing the broadest and richest vehicle data<br/><br/>The Geotab GO9 telematic vehicle tracking device captures and normalizes critical data from over 9,000 different cars, vans and trucks. Plug the GO device into any vehicle’s standard ODBII port and start capturing the industry’s highest quality vehicle data.
            </>}
            image={GoDeviceImage.src}
            smallImage
            pointsHeader='Key benefits of the GO device include:'
            points={[
              'Support for mixed fleets of different OEM makes and models',
              'Patented curve algorithm enables low bandwidth data transmission',
              'High resolution vehicle positioning for total fleet visibility',
              'Broad support for EVs and PHEVs'
            ]}
          />
          
          <ImagePointsSection 
            header='OEM Telematics'
            text={<>
              No installation, easy remote activation<br/><br/>Integrate OEM telematics from connected vehicles directly into the Geotab platform through the cloud. Geotab secures and normalizes the data stream to standardize it across other makes and models of vehicles.
            </>}
            image={OEMTelematicsImage.src}
            pointsHeader='Key benefits of OEM telematics include:'
            points={[
              'No additional hardware costs, no downtime for installation',
              'Easy, remote activation',
              'Guaranteed compatibility with vehicle',
              'Secure, end-to-end data transmission'
            ]}
          />

          <ImagePointsSection 
            switchDirection
            header='Mixed Fleets'
            text={<>
              Simplify mixed fleet management<br/><br/>Managing mixed fleets can be time-consuming for fleet managers. No matter the asset or vehicle type, make, model, or whether you are using telematics hardware or connected vehicle functionality to connect your vehicles, Geotab gives you the flexibility to manage your mixed fleet securely and easily on one platform.
            </>}
            image={MixedFleetsImage.src}
            points={[
              'Gain complete fleet and asset visibility and management tools for consistent management View all telematics and asset data in one AI-enabled view of performance and improvement areas',
              'Benefit from over 9000 vehicle and equipment makes and models and almost 300 EV supported',
              'Connect through GO device or any third party device, OEM telematics, and OEM-embedded'
            ]}
          />

          <div className={styles.geolabCTAHolder}>
            <h1 className={`${styles.ctaHeader} perfect-header`}>
              Insights to help fleets reduce emissions
            </h1>
            
            <p className={`${styles.ctaText} perfect-subtext`}>
              Implement sustainable fleet solutions while improving your bottom line.
            </p>

            <a className={styles.buttonHolder} href={links.geotab_link} rel='noreferrer' target='_blank'>
              <button className={`${styles.actionButton} blue-button action-button`} onClick={() => eventGTM('geotabScheduleDemoClick')}>
                Schedule a Demo
              </button>
            </a>
          </div>
        </div>

        <div className="image-section section" style={{ backgroundImage: `url(${GeolabAfter.src})` }}>&nbsp;</div>

        <div className={`${styles.powerHeaderHolder} padding`}>
          <h1 className={`${styles.powerHeader} page-header-h1`}>
            Power your sustainability journey with data-driven insights
          </h1>
        </div>
        
        <TheGreatSelector 
          options={options}
          selected={selected}
          setSelected={setSelected}
        />

        <div className={styles.powerPoints}>
          { selected === options[0] ? 
            <>
              <PointsSection 
                header='Mixed Fleets'
                text={<>Visualize sustainability efforts.<br/><br/>Compare your performance against similar fleets. Oversee your fleet’s operating costs and environmental performance with the Green Fleet Dashboard.</>}
                points={[
                  'Evaluate fuel economy by comparing to similar fleets',
                  'Study driving behavior trends to promote efficient driving',
                  'Track EV performance, usage and cost savings'
                ]}
              />
              <PointsSection 
                header='Reduce idling'
                text='Monitor the idling trends for your entire fleet with the Weekly Idle Cost Report.'
                points={[
                  'Calculate the amount of fuel wasted by idling',
                  'Attribute a cost to time spent idling',
                  'Monitor your carbon reduction initiatives'
                ]}
              />
              <PointsSection 
                header='Track fuel costs'
                text='Stay on top of your fuel expenses with the Fuel Tracker Add-in. Have drivers enter fuel purchases while they are at the pump, so you can track exactly which vehicle was filled-up where.'
                points={[
                  'Track fuel prices, cost per fill-up and monthly fuel spending',
                  'Map fuel purchases by vehicle, location and volume',
                  'Streamline your processes with fill-ups and IFTA report integration'
                ]}
              />
            </>
            : 
            <>
              <PointsSection 
                header='Take the most efficient route'
                text='Reduce unnecessary travel, increase asset utilization and improve fuel economy by optimizing routes with Add-ins from the Geotab Marketplace'
                points={[
                  'Save time on route planning and dispatching',
                  'Increase profitability by improving operational efficiency',
                  'Reduce fuel consumption and lower emissions'
                ]}
              />
              <PointsSection 
                header='Identify potential cost savings'
                text='Visualize your fleet’s performance and identify areas that can be targeted for improvement with the Global Fleet Savings Summary Report.'
                points={[
                  'Develop an overview of current and potential cost savings',
                  'Increase your fleet’s operating efficiency',
                  'Create coaching opportunities to improve driver behavior'
                ]}
              />
              <PointsSection 
                header='Reinforce safe driving'
                text='Eliminate risky or inefficient driving behaviors with the Driver Safety Scorecard Report. Prevent speeding, hard acceleration and harsh braking to improve efficiency.'
                points={[
                  'Proactively address unsafe and inefficient driving habits',
                  'Allow self management by objectively scoring drivers against their peers',
                  'Provide meaningful feedback for areas for improvement'
                ]}
              />
            </>
          }
        </div>

        <ActionSection 
          header="Arrange an initial call"
          text="Want to better understand your fleet’s needs? Contact us today to learn more about how Geotab’s cutting-edge telematics solutions can help your business achieve greater efficiency and profitability."
          buttonText="Contact Us"
          href="/contact-us"
        />

        <NevoBanner />
      </div>

      <Footer />
    </>
  )
};
